/**
 * سربرگِ «ترتیبِ پیشنهادی» — WHOSUP-285، مایگریشن 0157.
 *
 * ═══ قدمِ اولِ یک فرمول ═══
 *
 * قرار است روزی رویدادها با یک فرمول مرتب شوند: اول ترتیبِ پیشنهادیِ
 * شخصی‌نشده، بعد ترکیبِ اجزا، بعدتر شخصی‌سازی. این صفحه قدمِ اول است و
 * هنوز هیچ امتیازی نمی‌سازد: هر **جزء** را جدا نشان می‌دهد تا معلوم شود
 * کدام اصلاً داده دارد، کدام رویدادها را از هم جدا می‌کند، و کدام عملاً
 * همه‌جا صفر است.
 *
 * دو بخش دارد:
 *   • جدولِ اجزا — یک ردیف به ازای هر جزء: پوشش، تنوع، میانه، صدکِ ۹۰ و
 *     بیشینه. کلیک روی هر ردیف جدولِ پایین را با همان جزء مرتب می‌کند.
 *   • جدولِ رویدادها — همه‌ی اجزا کنارِ هم برای هر رویداد، مرتب‌شده با
 *     جزءِ انتخابی. «اگر فقط با همین جزء مرتب می‌کردیم، بالای فهرست چه
 *     می‌نشست» — همان سوالی که قبل از وزن دادن به هر جزء باید جواب بگیرد.
 *
 * ⚠ «ثانیه در صفحه» ستون دارد ولی خالی است و صفحه دلیلش را می‌گوید: امروز
 * هیچ‌جا ثبت نمی‌شود (توضیح سرِ مایگریشن 0157).
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import {
  COMPONENTS,
  GROUP_HELP,
  GROUP_LABEL,
  sortByComponent,
  summarize,
  type RankComponent,
} from '@/core/lib/rankReport';
import { fmtNum as fmt } from '@/ui/Num';
import { Tip } from './Tip';

/** عنوان، توضیح، و اینکه ستونِ عددی است. */
const SUMMARY_COLS: [string, string, boolean][] = [
  ['گروه', 'اجزا در سه دسته‌اند: رفتارِ کاربران، کیفیتِ محتوا، و زمینه. روی نامِ هر گروه بایست تا ببینی چه دارد.', false],
  ['جزء', 'یکی از چیزهایی که روزی می‌تواند در فرمولِ ترتیب بیاید. روی ردیف بزن تا جدولِ پایین با آن مرتب شود.', false],
  ['معلوم', 'چند درصدِ رویدادهای زنده برای این جزء مقدارِ معلوم دارند. نامعلوم با صفر فرق دارد: قیمتِ «See listing» نامعلوم است، رایگان صفر.', true],
  ['غیرصفر', 'چند درصدِ رویدادهای زنده مقدارشان صفر نیست. اگر کم باشد، این جزء فقط چند رویداد را از بقیه جدا می‌کند.', true],
  ['تنوع', 'چند مقدارِ متفاوت دارد. یک یعنی همه یکسان‌اند و این جزء هیچ رویدادی را از دیگری جدا نمی‌کند.', true],
  ['کمینه', 'کوچک‌ترین مقدارِ معلوم.', true],
  ['میانه', 'نیمی از رویدادها کمتر و نیمی بیشتر از این مقدارند.', true],
  ['صدکِ ۹۰', '۹۰٪ رویدادها این مقدار یا کمتر دارند؛ یعنی مرزِ ۱۰٪ بالایی.', true],
  ['بیشینه', 'بزرگ‌ترین مقدارِ معلوم. اگر خیلی از صدکِ ۹۰ دورتر است، چند رویدادِ استثنایی دارد.', true],
  ['میانگین', 'میانگینِ مقدارهای معلوم. اگر خیلی بالاتر از میانه است، چند رویدادِ بزرگ آن را بالا کشیده‌اند.', true],
];

/** جدولِ رویدادها چند ردیف نشان بدهد — بقیه فقط در خلاصه شمرده می‌شوند. */
const SHOW_ROWS = 200;

const pct = (part: number, whole: number) =>
  (whole ? `${Math.round((part / whole) * 100)}٪` : '—');

const val = (c: RankComponent, v: number | null) =>
  (v == null ? '—' : c.unit === '$' ? `$${fmt(v)}` : c.unit === '%' ? `${fmt(v)}٪` : fmt(v));

export function RankTab() {
  const q = useQuery({ queryKey: ['rank-stats'], queryFn: () => opsRepo.rankStats() });
  const [sortKey, setSortKey] = useState('views');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const rows = q.data?.rows ?? [];
  const summaries = useMemo(() => COMPONENTS.map((c) => summarize(rows, c)), [rows]);
  const sortComp = COMPONENTS.find((c) => c.key === sortKey) ?? COMPONENTS[0];
  const sorted = useMemo(
    () => sortByComponent(rows, sortComp, dir).slice(0, SHOW_ROWS),
    [rows, sortComp, dir],
  );

  if (q.isLoading) return <p className="dim">…</p>;
  if (q.error) return <div className="card"><p className="err">{String(q.error)}</p></div>;
  if (!q.data) return null;

  const d = q.data;
  const total = rows.length;

  const pick = (key: string) => {
    if (key === sortKey) setDir(dir === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setDir('desc'); }
  };

  return (
    <>
      <p className="note">
        هر رویدادِ <b>زنده</b> (منتشرشده، ادغام‌نشده، هنوز تمام‌نشده) و اجزایی که
        روزی فرمولِ ترتیب از آن‌ها ساخته می‌شود — هنوز بی‌وزن و بی‌امتیاز. ستونِ
        «تنوع» می‌گوید جزء چند مقدارِ متفاوت دارد؛ جزئی که همه‌جا یک عدد است
        هیچ رویدادی را از دیگری جدا نمی‌کند. حساب‌های تستی این‌جا کم نشده‌اند.
      </p>

      <div className="grid n4" style={{ marginTop: 12 }}>
        <div className="card stat">
          <div className="n">{fmt(d.matched)}</div>
          <div className="k"><Tip text="رویدادِ زنده" help="منتشرشده، از نوعِ رویداد، ادغام‌نشده و هنوز تمام‌نشده (یا بی‌تاریخ، یا با اجرای آینده)." /></div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(rows.filter((r) => r.viewers > 0).length)}</div>
          <div className="k"><Tip text="دست‌کم یک بیننده" help="چند رویدادِ زنده را دست‌کم یک کاربرِ واردشده باز کرده است." /></div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(rows.filter((r) => r.plans > 0).length)}</div>
          <div className="k"><Tip text="دست‌کم یک پلن" help="از روی چند رویدادِ زنده دست‌کم یک پلنِ پاک‌نشده ساخته شده." /></div>
        </div>
        <div className="card stat">
          <div className="n">{fmt(new Set(rows.map((r) => r.source)).size)}</div>
          <div className="k"><Tip text="منبع" help="این رویدادهای زنده از چند منبعِ مختلف آمده‌اند." /></div>
        </div>
      </div>

      {d.matched > d.shown ? (
        <p className="note">
          {fmt(d.matched)} رویدادِ زنده هست ولی فقط {fmt(d.shown)} تا آمد — آمارِ
          پایین فقط روی همین‌هاست.
        </p>
      ) : null}

      <div className="card">
        <h2>اجزا</h2>
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                {SUMMARY_COLS.map(([text, help, num]) => (
                  <th key={text} className={num ? 'num' : undefined}><Tip text={text} help={help} /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPONENTS.map((c, i) => {
                const s = summaries[i];
                const flat = !c.unmeasured && s.known > 0 && s.distinct <= 1;
                return (
                  <tr key={c.key} onClick={() => pick(c.key)}
                    style={{ cursor: 'pointer', fontWeight: c.key === sortKey ? 700 : undefined }}>
                    <td className="dim"><Tip text={GROUP_LABEL[c.group]} help={GROUP_HELP[c.group]} /></td>
                    <td>
                      <Tip text={c.unit ? `${c.label} (${c.unit})` : c.label} help={c.help} />
                      {c.unmeasured ? <span className="chip warn">ثبت نمی‌شود</span> : null}
                      {flat ? <span className="chip warn">یکسان</span> : null}
                    </td>
                    <td className="num">{c.unmeasured ? '—' : pct(s.known, total)}</td>
                    <td className="num">{c.unmeasured ? '—' : pct(s.nonZero, total)}</td>
                    <td className="num">{c.unmeasured ? '—' : fmt(s.distinct)}</td>
                    <td className="num">{val(c, s.min)}</td>
                    <td className="num">{val(c, s.median)}</td>
                    <td className="num">{val(c, s.p90)}</td>
                    <td className="num">{val(c, s.max)}</td>
                    <td className="num">{val(c, s.mean)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!d.dwell_measured ? (
          <p className="dim" style={{ marginTop: 8 }}>
            «ثانیه در صفحه» امروز هیچ‌جا ثبت نمی‌شود: رویدادِ صفحه فقط الگوی مسیر را
            دارد و نه شناسه‌ی رویداد. ستونش می‌ماند تا وقتی اپ این را بفرستد.
          </p>
        ) : null}
      </div>

      <div className="card">
        <h2>
          رویدادها، مرتب با «{sortComp.label}» {dir === 'desc' ? '↓' : '↑'}
        </h2>
        <p className="dim" style={{ marginBottom: 8 }}>
          {fmt(Math.min(SHOW_ROWS, total))} از {fmt(total)} — روی سرِ هر ستون (یا ردیفِ
          جدولِ بالا) بزن تا با آن مرتب شود؛ دوباره بزن تا جهتش برگردد. مقدارِ
          نامعلوم همیشه ته می‌نشیند.
        </p>
        <div className="scrollx">
          <table className="tbl">
            <thead>
              <tr>
                <th><Tip text="رویداد" help="عنوانِ رویداد با لینک به صفحه‌ی اصلی‌اش در منبع، و تاریخِ شروع زیرش." /></th>
                <th><Tip text="منبع" help="نامِ منبعی (سایتی) که خزنده این رویداد را از آن برداشته." /></th>
                {COMPONENTS.filter((c) => !c.unmeasured).map((c) => (
                  <th key={c.key} className="num"
                    style={{ cursor: 'pointer', fontWeight: c.key === sortKey ? 800 : undefined }}
                    onClick={() => pick(c.key)}>
                    <Tip text={c.label} help={c.help} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.url ? (
                      <a href={r.url} target="_blank" rel="noreferrer">{r.title}</a>
                    ) : r.title}
                    {r.starts_at ? (
                      <div className="dim ltr">{new Date(r.starts_at).toLocaleDateString('en-CA')}</div>
                    ) : null}
                  </td>
                  <td className="mono">{r.source}</td>
                  {COMPONENTS.filter((c) => !c.unmeasured).map((c) => (
                    <td key={c.key} className="num">{val(c, c.value(r))}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
